import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/cn";

export type ButtonVariant =
  "primary" | "secondary" | "ghost" | "danger" | "quiet";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

export function buttonStyles(
  variant: ButtonVariant = "secondary",
  size: ButtonSize = "md",
  className?: string,
): string {
  return cn(
    "relative inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-200 select-none",
    "disabled:cursor-not-allowed disabled:opacity-45 disabled:saturate-50",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07080b]",
    {
      "bg-gradient-to-b from-violet-500 to-violet-600 text-white shadow-[0_8px_28px_-8px_rgba(139,92,246,0.7),inset_0_1px_0_rgba(255,255,255,0.16)] hover:from-violet-400 hover:to-violet-500 hover:shadow-[0_10px_34px_-8px_rgba(139,92,246,0.85)] active:translate-y-px":
        variant === "primary",
      "border border-white/10 bg-white/[0.045] text-zinc-100 hover:border-white/[0.16] hover:bg-white/[0.075] active:translate-y-px":
        variant === "secondary",
      "text-zinc-400 hover:bg-white/[0.05] hover:text-white":
        variant === "ghost",
      "border border-rose-400/20 bg-rose-500/10 text-rose-200 hover:border-rose-400/30 hover:bg-rose-500/15":
        variant === "danger",
      "bg-transparent text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100":
        variant === "quiet",
      "h-8 px-3 text-[13px]": size === "sm",
      "h-10 px-4 text-sm": size === "md",
      "h-12 px-5 text-[15px]": size === "lg",
      "size-9 p-0": size === "icon",
    },
    className,
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "secondary",
      size = "md",
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        className={buttonStyles(variant, size, className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="size-4 animate-spin" />}
        {children}
      </button>
    );
  },
);
